import type { GameStateManager } from '../game/GameState';
import type { GameData, InsanityResult, GameSpeed } from '../game/types';
import { AudioManager } from '../audio/AudioManager';

export class InsanityModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onClose: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Enter' || event.code === 'Escape' || event.code === 'Space') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(result: InsanityResult): void {
    this.render(result);
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    AudioManager.getInstance().playEffect();
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    this.gameState.dequeueInsanity();
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    // Defer onClose to next tick to prevent synchronous re-showing of same modal
    setTimeout(() => {
      this.onClose();
    }, 0);
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private render(result: InsanityResult): void {
    const strings = this.gameData.uiStrings;

    // Handle null/empty values gracefully
    const researcherName = result.researcherName?.trim() || 'Unknown Researcher';
    const locationName = result.locationName?.trim() || 'Unknown Location';
    const reason = result.reason?.trim() || 'Unknown reason';

    // Build affected researchers list
    let affectedList = '';
    if (result.affectedResearchers.length > 0) {
      affectedList = result.affectedResearchers.map(affected => {
        const name = affected.name?.trim() || 'Unknown Researcher';
        return `<div class="effect-item" style="border-left-color: #ff4444; color: #ff4444">
          ${this.escapeHtml(name)}: -${affected.damage.toFixed(2)} Sanity
        </div>`;
      }).join('');
    } else {
      affectedList = '<div class="effect-item">No other researchers were affected</div>';
    }

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content insanity-modal" tabindex="0">
        <div class="modal-header">
          <h2>Insanity</h2>
          <button class="modal-close" data-action="close">×</button>
        </div>
        <div class="modal-body">
          <div class="insanity-info">
            <div class="completion-section">
              <h3>Researcher</h3>
              <div>${this.escapeHtml(researcherName)}</div>
            </div>
            
            <div class="completion-section">
              <h3>Location</h3>
              <div>${this.escapeHtml(locationName)}</div>
            </div>
            
            <div class="completion-section">
              <h3>Reason</h3>
              <div style="color: #ff4444; font-weight: bold;">${this.escapeHtml(reason)}</div>
            </div>
            
            ${result.affectedResearchers.length > 0 ? `
            <div class="completion-section">
              <h3>Sanity Damage Caused</h3>
              <div class="effects-list">
                ${affectedList}
              </div>
              <div style="margin-top: 10px; font-weight: bold; color: #ff4444;">
                Total Sanity Damage: ${result.totalSanityDamage.toFixed(2)}
              </div>
            </div>
            ` : ''}
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-confirm" data-action="close">Close</button>
        </div>
      </div>
    `;

    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const footerCloseButton = this.container.querySelector('.modal-footer [data-action="close"]') as HTMLElement;
    if (footerCloseButton) {
      footerCloseButton.addEventListener('click', () => this.hide());
    }
  }
}
